import { useDeviceType } from "@dash/mq";
import { SelectDropdown } from "@dash/select";
import { useQueryClient } from "react-query";

import { useClientManager } from "@megaron/react-clients";
import {
  isTicketStatus,
  TicketListItemDto,
  TicketStatus,
  ticketStatuses,
  ticketStatusLabels,
} from "@megaron/support-contracts";

import { useSelectableSupportTicketStatuses } from "../shared/useSupportTicketEditable";

function buildUpdateBody(item: TicketListItemDto, status: TicketStatus) {
  return {
    id: item.id,
    subject: item.subject,
    status,
    priority: item.priority ?? null,
    category: item.category ?? null,
    tags: item.tags,
    invoiceNumber: undefined,
    orderNumber: undefined,
    reporterName: undefined,
    reporterEmail: undefined,
    reporterPhone: undefined,
  };
}

type Props = {
  item: TicketListItemDto;
  compact?: boolean;
};

const statusOptions = ticketStatuses.map((status) => ({
  value: status,
  label: ticketStatusLabels[status],
}));

export function SupportTicketListStatusSelect({ item, compact = false }: Props) {
  const { isMobile } = useDeviceType();
  const queryClient = useQueryClient();
  const selectableStatuses = useSelectableSupportTicketStatuses(item.status);
  const updateMutation = useClientManager("support").updateTicket().useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support", "ticketsList"] });
      queryClient.invalidateQueries({ queryKey: ["support", "getTicket"] });
    },
  });

  const handleChange = (raw: string) => {
    if (!isTicketStatus(raw) || raw === item.status) return;
    updateMutation.mutate(buildUpdateBody(item, raw));
  };

  return (
    <div
      css={
        compact
          ? {
              width: "100%",
              minWidth: "4.75rem",
              maxWidth: isMobile ? "100%" : "7rem",
              "& button": {
                padding: "0.25rem 0.375rem",
                fontSize: "0.8125rem",
                gap: "0.25rem",
              },
              "& button span:first-of-type": {
                fontSize: "0.8125rem",
              },
              "& svg": {
                width: "16px",
                height: "16px",
              },
            }
          : {
              width: "100%",
              minWidth: "7rem",
              maxWidth: isMobile ? "100%" : "10rem",
            }
      }
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <SelectDropdown
        value={isTicketStatus(item.status) ? item.status : undefined}
        options={statusOptions.filter((option) => selectableStatuses.includes(option.value))}
        onChange={handleChange}
        isDisabled={updateMutation.isLoading || selectableStatuses.length <= 1}
      />
    </div>
  );
}
